import mustache from "mustache";
import { MonitoreoPlagaEnfermedadItem } from "../../../../../generated/grpc/catalogos_monitoreo_pb";
import {
  formatDiaMes,
  formatRangoSemana,
  getDatosDeRecorrido,
  LoteMonitoreado,
} from "../../../../../modulos/monitoreo/sesion/agruparPorSemana";
import { formatNivel } from "../../../../../modulos/monitoreo/sesion/formatIncidencia";
import {
  ESTADO_LABEL,
  evaluarEnLote,
  getAbreviatura,
  getPeorPlagaEvaluada,
  SeleccionPlagas,
} from "../../../../../modulos/monitoreo/sesion/semaforo";
import { SemanaMonitoreoItem } from "../../../../../modulos/monitoreo/sesion/services/SesionSemanaGroupListByFincaIdService";
import { BaseSecondSidebarItemRender } from "../../../../base/sidebar/BaseSecondSidebarItemRender";
import {
  COLOR_SIN_REFERENCIA,
  ESTADO_COLOR,
} from "../dashboard/monitoreoAnalisisPaleta";
import template from "./monitoreoSesionSemanaItemTemplate.html";

mustache.parse(template);

/** Contador incremental de instancias, para no colisionar ids en el DOM. */
let ITEM_ID = 0;

/** Item del sidebar de la vista de sesión: una semana ISO.
 *
 *  El label lleva la semana y su **rango de calendario** ("Semana 35 ·
 *  24–30 ago"), no la fecha de un recorrido: la semana puede cubrirse en varias
 *  salidas y poner una de ellas daría a entender que la semana es ese día.
 *
 *  Dentro van los **chips de fecha**: al elegir uno, el mapa muestra solo los
 *  lotes visitados esa fecha — el equivalente a elegir un día en Aplicaciones,
 *  donde el mapa marca el vuelo de ese día. "Toda la semana" (lo fusionado)
 *  aparece solo si hubo más de una salida; las fechas, siempre, porque además
 *  de marcar el mapa son el acceso al informe de ese día y eso no depende de
 *  cuántos recorridos hubo.
 *
 *  Al pie, un botón que **sigue al chip activo**: "Informe del 26 ago" o
 *  "Informe de la semana". Uno solo, y no una acción por chip, para que no se
 *  pueda pedir el informe de un día distinto del que se está viendo.
 *
 *  La lista de lotes usa el valor de las **plagas activas** —las mismas que
 *  colorean el mapa— para que sidebar y mapa cuenten siempre lo mismo: con una
 *  sola plaga elegida muestra su valor, y con un grupo o en modo "Todas" la peor
 *  plaga de cada lote dentro de la selección, que es de donde sale el color del
 *  polígono. Por eso el sidebar tiene que poder pedirle un {@link refresh} al
 *  cambiar la selección: si no, la lista se queda con los valores de la
 *  anterior. */
export class MonitoreoSesionSemanaItemRender extends BaseSecondSidebarItemRender {
  /**
   * @param _semana    Semana que representa el item.
   * @param _onSelecda Notifica semana + recorrido elegido (`null` = toda).
   * @param _getSeleccion  Plagas activas al renderizar el contenido (`null` =
   *                       modo "Todas").
   * @param _getPlagasById Catálogo completo, que "Todas" y los grupos necesitan
   *                       para resolver la peor plaga de cada lote.
   * @param _onVerInforme  Pide el informe de lo que esté seleccionado: el día
   *                       elegido, o la semana entera.
   */
  constructor(
    private readonly _semana: SemanaMonitoreoItem,
    private readonly _onSelecda: (
      semana: SemanaMonitoreoItem,
      sesionId: string | null,
    ) => void,
    private readonly _getSeleccion: () => SeleccionPlagas,
    private readonly _getPlagasById: () => Map<
      string,
      MonitoreoPlagaEnfermedadItem
    >,
    private readonly _onVerInforme: (
      semana: SemanaMonitoreoItem,
      sesionId: string | null,
    ) => void,
  ) {
    super();
    ITEM_ID += 1;
    this._chipsId = ITEM_ID;
  }

  /** "Semana 35 · 24–30 ago". */
  protected getLabel(): string {
    return `Semana ${this._semana.semana} · ${formatRangoSemana(this._semana)}`;
  }

  /** Sin título extra: cada renglón ya dice su lote. */
  protected getTitle(): string {
    return "";
  }

  /** Contenido expandido, envuelto en un contenedor propio para poder
   *  repintarlo (ver {@link refresh}). */
  protected getItemContent(): string {
    return `<div id="monSesBody-${this._chipsId}">${this._buildBody()}</div>`;
  }

  /** Vuelve a pintar el contenido si el item está expandido — lo llama el
   *  sidebar cuando cambia la plaga activa.
   *
   *  Sin esto, cambiar de plaga repintaba el mapa y dejaba la lista con los
   *  valores de la plaga anterior: sidebar y mapa contando cosas distintas,
   *  que es justo lo que este item existe para evitar. */
  refresh(): void {
    const contenedor = document.getElementById(`monSesBody-${this._chipsId}`);
    // Ausente si el item está colapsado o ya dispuesto: no hay nada que hacer.
    if (!contenedor) return;

    this.disposeExpanded();
    contenedor.innerHTML = this._buildBody();
    this.connectExpanded();
  }

  /** Chips de fecha (si corresponde) + una fila por lote monitoreado. */
  private _buildBody(): string {
    const seleccion = this._getSeleccion();
    // Una sola plaga elegida: la fila puede mostrar su valor crudo sin
    // ambigüedad. Con un grupo o con "Todas" hay varias en juego y ninguna
    // representa al lote por sí sola.
    const plaga =
      seleccion?.length === 1
        ? this._getPlagasById().get(seleccion[0])
        : undefined;
    const datos = getDatosDeRecorrido(this._semana, this._sesionId) ?? {};

    const lotes = Object.entries(datos)
      .map(([loteId, porPlaga]) => {
        const nombre = this._semana.loteNombres[loteId] ?? loteId;
        // Modo "Todas" o un grupo: la fila muestra la peor plaga del lote
        // —abreviada, que para eso existe `getAbreviatura`— para decir de dónde
        // viene el color, igual que la etiqueta del polígono.
        if (!plaga) return this._filaPeor(nombre, porPlaga, seleccion);

        // El lote está en `datos`, así que fue monitoreado: una plaga ausente
        // del mapa significa que no se detectó, o sea 0.
        const evaluacion = evaluarEnLote(porPlaga, plaga.id, plaga);
        if (!evaluacion || evaluacion.estado === null) {
          // Benéfico, o sin valores de referencia: hay número pero no color, y
          // el gris de "no se puede evaluar" no es el verde de "está bien".
          return {
            nombre,
            valor: evaluacion ? formatNivel(evaluacion) : "—",
            titulo: `${plaga.nombreComun} — sin valores de referencia`,
            color: COLOR_SIN_REFERENCIA,
          };
        }
        return {
          nombre,
          valor: formatNivel(evaluacion),
          titulo: `${plaga.nombreComun} — ${ESTADO_LABEL[evaluacion.estado]}`,
          color: ESTADO_COLOR[evaluacion.estado],
        };
      })
      .sort((a, b) => a.nombre.localeCompare(b.nombre));

    // "Toda la semana" solo se ofrece si hubo más de una salida: con una sola,
    // el día y la semana son la misma cosa dicha dos veces. Las fechas en sí
    // se muestran siempre, porque además de marcar el mapa son el acceso al
    // informe de ese día, y eso no depende de cuántas salidas hubo.
    const conRecorridos = this._semana.sesiones.length > 1;
    const sesionActiva = this._getSesionActiva();

    return mustache.render(template, {
      id: this._chipsId,
      conRecorridos,
      todaActiva: sesionActiva === null,
      recorridos: this._semana.sesiones.map((sesion) => ({
        sesionId: sesion.id,
        etiqueta: formatDiaMes(sesion.fecha),
        activa: sesion.id === sesionActiva,
      })),
      lotes,
      mensajeVacio: conRecorridos
        ? "Sin lotes en este recorrido."
        : "Sin lotes monitoreados esta semana.",
      etiquetaInforme: this._getEtiquetaInforme(sesionActiva),
    });
  }

  /**
   * Recorrido que está mirando el usuario.
   *
   * Con una sola salida en la semana, `_sesionId` queda en `null` —nunca se
   * tocó un chip— pero lo que se ve **es** ese recorrido: se resuelve acá para
   * que el chip marcado, la etiqueta del botón y el informe que se abre salgan
   * los tres del mismo valor y no puedan contradecirse.
   */
  private _getSesionActiva(): string | null {
    if (this._sesionId) return this._sesionId;
    return this._semana.sesiones.length === 1
      ? this._semana.sesiones[0].id
      : null;
  }

  /** "Informe del 26 ago" o "Informe de la semana", según lo seleccionado. */
  private _getEtiquetaInforme(sesionActiva: string | null): string {
    if (!sesionActiva) return "Informe de la semana";
    const sesion = this._semana.sesiones.find((s) => s.id === sesionActiva);
    return sesion
      ? `Informe del ${formatDiaMes(sesion.fecha)}`
      : "Informe de la semana";
  }

  /** Cablea los chips de fecha y el botón del informe (se reinyectan en cada
   *  re-render). */
  protected connectExpanded(): void {
    this._fechasEl = document.getElementById(`monSesFechas-${this._chipsId}`);
    this._fechasEl?.addEventListener("click", this._onFechaClick);

    this._informeBtn = document.getElementById(
      `monSesInforme-${this._chipsId}`,
    );
    this._informeBtn?.addEventListener("click", this._onInformeClick);
  }

  protected disposeExpanded(): void {
    this._fechasEl?.removeEventListener("click", this._onFechaClick);
    this._fechasEl = null;
    this._informeBtn?.removeEventListener("click", this._onInformeClick);
    this._informeBtn = null;
  }

  /** Al expandirse, el mapa muestra la semana completa. */
  protected onSelected(): void {
    this._sesionId = null;
    this._onSelecda(this._semana, null);
  }

  /** Id de instancia para los chips. Lleva nombre propio porque
   *  `BaseSecondSidebarItemRender` ya tiene un `_itemId` privado. */
  private readonly _chipsId: number;
  /** Recorrido elegido, o `null` para la semana completa. */
  private _sesionId: string | null = null;
  /** Contenedor de los chips (disponible mientras el item está expandido). */
  private _fechasEl: HTMLElement | null = null;
  /** Botón del informe (disponible mientras el item está expandido). */
  private _informeBtn: HTMLElement | null = null;

  /** Abre el informe de lo que esté seleccionado. Un solo control en vez de una
   *  acción por chip: así no puede pedirse el informe de un día distinto del
   *  que se está viendo en el mapa. */
  private _onInformeClick = (): void => {
    this._onVerInforme(this._semana, this._getSesionActiva());
  };

  private _onFechaClick = (event: Event): void => {
    const chip = (event.target as HTMLElement).closest<HTMLElement>(
      "[data-sesion]",
    );
    if (!chip) return;

    // `data-sesion=""` es "toda la semana".
    const sesionId = chip.dataset.sesion || null;
    if (sesionId === this._sesionId) return;
    this._sesionId = sesionId;

    // Re-render del contenido: cambian los chips activos y la lista de lotes.
    this.refresh();
    this._onSelecda(this._semana, sesionId);
  };

  /** Fila del lote cuando hay varias plagas en juego —modo "Todas" o un grupo—:
   *  la peor **dentro de la selección** con su valor, o un guion si se recorrió
   *  y no se detectó nada de lo seleccionado (que no es lo mismo que no
   *  recorrerlo — ese lote ni aparece en `datos`). */
  private _filaPeor(
    nombre: string,
    lote: LoteMonitoreado,
    seleccion: SeleccionPlagas,
  ): { nombre: string; valor: string; titulo: string; color: string } {
    const peor = getPeorPlagaEvaluada(lote, this._getPlagasById(), seleccion);
    if (!peor) {
      return {
        nombre,
        valor: "—",
        titulo: "Monitoreado, sin detección",
        color: ESTADO_COLOR.ok,
      };
    }

    return {
      nombre,
      valor: `${getAbreviatura(peor.plaga)} ${formatNivel(peor.evaluacion)}`,
      titulo: `${peor.plaga.nombreComun} — ${ESTADO_LABEL[peor.estado]}`,
      color: ESTADO_COLOR[peor.estado],
    };
  }
}

